import { useState, useMemo, useEffect } from 'react';
import {
  Search,
  Clock,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Zap,
  Workflow,
  Brain,
  GitBranch,
  Database,
  Key,
  Wrench,
  MessageSquare,
  Plug,
  FileJson,
  HardDrive,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { getAllNodeDefinitions, type NodeDefinition } from '@/utils/nodeDefinitions';
import { cn } from '@/lib/utils';

interface EnhancedNodePaletteProps {
  onAddNode?: (nodeType: string) => void;
  onNodeSelect?: (nodeType: string) => void;
  initialCategory?: string;
  compact?: boolean;
  subNodeFilter?: 'chatModel' | 'memory' | 'tools' | 'tool' | 'parser' | 'pollSource'; // Filter to show only specific sub-node types
  visibleCategories?: string[]; // Dynamically control which categories are visible
  onClose?: () => void; // Callback when palette is closed
  isOpen?: boolean; // Controlled open state from parent
  forceClose?: number; // Increment to trigger palette close (for one-time close events)
}

// Category definitions - Recent first, then alphabetically sorted
const categories = [
  { id: 'recent', label: 'Recent', icon: Clock, description: 'Recently used nodes' },
  { id: 'access-integrations', label: 'Access Integrations', icon: Key, description: 'Access platform integrations' },
  { id: 'ai-nodes', label: 'AI Nodes', icon: Brain, description: 'AI and LangChain nodes' },
  { id: 'database', label: 'Database', icon: Database, description: 'Database connections' },
  { id: 'files', label: 'Files', icon: HardDrive, description: 'File transfer and storage' },
  { id: 'flow-control', label: 'Flow Control', icon: GitBranch, description: 'Control flow logic' },
  { id: 'integrations', label: 'Integrations', icon: Key, description: 'External services' },
  { id: 'responses', label: 'Responses', icon: MessageSquare, description: 'Response and output nodes' },
  { id: 'tools', label: 'Tools', icon: Plug, description: 'External tools and capabilities' },
  { id: 'triggers', label: 'Triggers', icon: Zap, description: 'Start your workflow' },
  { id: 'utilities', label: 'Utilities', icon: Wrench, description: 'Data and utility operations' },
  { id: 'web', label: 'Web', icon: Workflow, description: 'Web-related nodes' },
  // Hidden anchor categories - only shown when clicking anchor buttons
  { id: 'anchor-tools', label: 'Tools', icon: Wrench, description: 'AI Agent tools', hidden: true },
  { id: 'anchor-chatModels', label: 'Chat Models', icon: Brain, description: 'AI chat models', hidden: true },
  { id: 'anchor-memory', label: 'Memory', icon: Database, description: 'Memory systems', hidden: true },
  { id: 'anchor-parser', label: 'Output Parsers', icon: FileJson, description: 'Agent output parsers', hidden: true },
  { id: 'anchor-pollSource', label: 'Data Sources', icon: Database, description: 'Nodes that can be polled for data', hidden: true },
];

// Get all nodes and organize by category
const allNodeDefinitions = getAllNodeDefinitions();
const nodesByCategory = allNodeDefinitions.reduce((acc, node) => {
  // Add node to primary category
  if (!acc[node.category]) {
    acc[node.category] = [];
  }
  acc[node.category].push(node);
  
  // Add node to additional categories if specified
  if (node.categories) {
    node.categories.forEach(cat => {
      if (cat !== node.category) { // Don't duplicate in primary category
        if (!acc[cat]) {
          acc[cat] = [];
        }
        acc[cat].push(node);
      }
    });
  }
  
  return acc;
}, {} as Record<string, NodeDefinition[]>);

function EnhancedNodePalette({ onAddNode, onNodeSelect, initialCategory, compact = false, subNodeFilter, visibleCategories, onClose, isOpen, forceClose }: EnhancedNodePaletteProps = {}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentView, setCurrentView] = useState<'categories' | string>('categories');
  const [recentNodes, setRecentNodes] = useState<string[]>([]);
  const [isExpandedInternal, setIsExpandedInternal] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [expandedDescription, setExpandedDescription] = useState<string | null>(null);

  // Use controlled state if provided, otherwise use internal state
  const isExpanded = isOpen !== undefined ? isOpen : isExpandedInternal;
  const setIsExpanded = (value: boolean) => {
    setIsExpandedInternal(value);
    // If parent is controlling, they'll handle it via isOpen prop
  };

  // Handle forceClose prop - close palette when it changes (increments)
  useEffect(() => {
    if (forceClose && forceClose > 0) {
      setIsExpandedInternal(false);
    }
  }, [forceClose]);
  
  // Filter categories based on visibility
  const visibleCategoryList = useMemo(() => {
    if (!visibleCategories) {
      // Default: show all non-hidden categories
      return categories.filter(cat => !cat.hidden);
    }
    // Show only specified categories
    return categories.filter(cat => visibleCategories.includes(cat.id));
  }, [visibleCategories]);

  // Load recent nodes from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem('recentNodes');
      if (stored) {
        setRecentNodes(JSON.parse(stored));
      }
    } catch (error) {
    }
  }, []);

  // Handle initialCategory prop - open the palette to that category
  useEffect(() => {
    if (initialCategory) {
      setSearchQuery(''); // Clear search when opening to a specific category
      setCurrentView(initialCategory);
      setIsExpanded(true);
    }
  }, [initialCategory]);

  // Handle subNodeFilter - automatically show filtered nodes
  useEffect(() => {
    if (subNodeFilter) {
      setSearchQuery(''); // Clear search when filtering for sub-nodes
      setCurrentView('filtered');
      setIsExpanded(true);
    }
  }, [subNodeFilter]);

  // Handle visibleCategories change - clear search when anchor menu opens
  useEffect(() => {
    if (visibleCategories) {
      setSearchQuery(''); // Clear search when opening from anchor "+" button
    }
  }, [visibleCategories]);
  
  // Call onClose when palette is collapsed
  useEffect(() => {
    if (!isExpanded && onClose) {
      onClose();
    }
  }, [isExpanded, onClose]);

  // Save recent nodes to localStorage
  const addToRecent = (nodeType: string) => {
    const updated = [nodeType, ...recentNodes.filter((t) => t !== nodeType)].slice(0, 5);
    setRecentNodes(updated);
    try {
      localStorage.setItem('recentNodes', JSON.stringify(updated));
    } catch (error) {
    }
  };

  // Get all nodes for search
  const allNodes = useMemo(() => {
    const nodes: NodeDefinition[] = [];
    const hiddenCategories = categories.filter(cat => cat.hidden).map(cat => cat.id);
    
    Object.entries(nodesByCategory).forEach(([category, categoryNodes]) => {
      // Skip nodes from hidden categories in search
      if (hiddenCategories.includes(category)) {
        return;
      }
      
      categoryNodes.forEach((node) => {
        nodes.push(node);
      });
    });
    
    // Apply sub-node filter if specified
    if (subNodeFilter) {
      return nodes.filter((node) => {
        // Check if node is a sub-node with matching category
        return node.isSubNode && 
               node.subNodeConfig?.category === subNodeFilter;
      });
    }
    
    return nodes;
  }, [subNodeFilter, nodesByCategory, categories]);

  // Filter nodes based on search and coming soon toggle
  const filteredNodes = useMemo(() => {
    if (!searchQuery) return [];
    const query = searchQuery.toLowerCase();
    let filtered = allNodes.filter(
      (node) =>
        node.label.toLowerCase().includes(query) ||
        node.description.toLowerCase().includes(query) ||
        node.type.toLowerCase().includes(query)
    );
    
    // Apply coming soon filter
    if (!showComingSoon) {
      filtered = filtered.filter((node) => !node.comingSoon);
    }
    
    return filtered;
  }, [searchQuery, allNodes, showComingSoon]);

  // Get recent nodes data
  const recentNodesData = useMemo(() => {
    return recentNodes
      .map((type) => allNodes.find((n) => n.type === type))
      .filter(Boolean) as NodeDefinition[];
  }, [recentNodes, allNodes]);

  const handleDragStart = (event: React.DragEvent, nodeType: string) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'copy';
    addToRecent(nodeType);

    // Dispatch custom event so canvas knows what's being dragged (for drop-on-node feature)
    window.dispatchEvent(new CustomEvent('nodepalette:dragstart', {
      detail: { nodeType }
    }));

    // Get node definition for styling
    const nodeDefinition = allNodes.find(n => n.type === nodeType);
    if (!nodeDefinition) return;

    const isComplexNode = ['ai-agent', 'aiAgent', 'ai', 'langchain-rag-chain'].includes(nodeType) || 
                          nodeDefinition.category === 'ai-nodes';
    const nodeSize = isComplexNode ? 128 : 96;

    // Create a container for the drag preview (node + label)
    const dragPreviewContainer = document.createElement('div');
    dragPreviewContainer.style.cssText = `
      position: absolute;
      top: -10000px;
      left: -10000px;
      display: flex;
      flex-direction: column;
      align-items: center;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    `;

    // Create the node card (matches GenericNode exactly)
    const dragPreview = document.createElement('div');
    dragPreview.style.cssText = `
      width: ${nodeSize}px;
      height: ${nodeSize}px;
      box-sizing: border-box;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(to bottom right, ${nodeDefinition.bgColor}, ${nodeDefinition.bgColor});
      border: 2px solid ${nodeDefinition.borderColor};
      border-radius: 0.5rem;
      box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    `;

    // Add icon (clone from the palette item)
    const target = event.currentTarget as HTMLElement;
    const iconElement = target.querySelector('svg');
    if (iconElement) {
      const iconClone = iconElement.cloneNode(true) as SVGElement;
      iconClone.style.cssText = `
        width: ${isComplexNode ? '40px' : '32px'};
        height: ${isComplexNode ? '40px' : '32px'};
        color: ${nodeDefinition.color};
      `;
      dragPreview.appendChild(iconClone);
    }

    dragPreviewContainer.appendChild(dragPreview);

    // Add label below the node
    const label = document.createElement('div');
    label.textContent = nodeDefinition.label;
    label.style.cssText = `
      margin-top: 8px;
      font-size: 12px;
      font-weight: 500;
      color: #111827;
      text-align: center;
      max-width: ${isComplexNode ? '140px' : '120px'};
    `;
    dragPreviewContainer.appendChild(label);

    document.body.appendChild(dragPreviewContainer);

    try {
      event.dataTransfer.setDragImage(dragPreviewContainer, nodeSize / 2, nodeSize / 2);
    } catch (e) {
    }

    // Clean up after drag starts
    setTimeout(() => {
      if (document.body.contains(dragPreviewContainer)) {
        document.body.removeChild(dragPreviewContainer);
      }
    }, 0);
  };

  const handleDragEnd = () => {
    // Dispatch custom event to clear highlighting on canvas
    window.dispatchEvent(new CustomEvent('nodepalette:dragend'));
  };

  const handleNodeClick = (nodeType: string) => {
    // If onNodeSelect is provided (e.g., for insert node dialog), use that
    if (onNodeSelect) {
      onNodeSelect(nodeType);
      addToRecent(nodeType);
    } else if (onAddNode) {
      // Otherwise use onAddNode (normal drag-and-drop behavior)
      onAddNode(nodeType);
      addToRecent(nodeType);
    }
  };

  const renderCategoryView = () => (
    <div className="space-y-2 p-2 pb-4">
      {visibleCategoryList.map((category) => {
        const Icon = category.icon;
        const nodeCount =
          category.id === 'recent'
            ? recentNodesData.length
            : nodesByCategory[category.id as keyof typeof nodesByCategory]?.length || 0;

        return (
          <Card
            key={category.id}
            className="p-4 cursor-pointer hover:bg-accent transition-colors"
            onClick={() => setCurrentView(category.id)}
          >
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Icon className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-medium text-sm">{category.label}</h3>
                  <span className="text-xs text-muted-foreground">{nodeCount}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{category.description}</p>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );

  const renderNodeList = (nodes: Array<{ type: string; label: string; icon: any; description: string; comingSoon?: boolean; isSubNode?: boolean; subNodeConfig?: { category: string }; toolCapable?: { enabled: boolean } }>) => (
    <div className="space-y-2 p-2 pb-4">
      {nodes.map((node) => {
        const Icon = node.icon;
        const isComingSoon = node.comingSoon;
        const isSubNode = node.isSubNode;
        const isToolCapable = node.toolCapable?.enabled;
        
        // Determine border color based on node type
        let borderColor = '';
        
        // Sub-nodes get colored borders based on their category
        if (isSubNode && node.subNodeConfig) {
          switch (node.subNodeConfig.category) {
            case 'chatModel':
              borderColor = 'border-l-4 border-l-purple-500';
              break;
            case 'memory':
              borderColor = 'border-l-4 border-l-blue-500';
              break;
            case 'tool':
              borderColor = 'border-l-4 border-l-green-500';
              break;
            case 'parser':
              borderColor = 'border-l-4 border-l-orange-500';
              break;
          }
        }
        // Tool-capable nodes (that aren't sub-nodes) get green border
        else if (isToolCapable) {
          borderColor = 'border-l-4 border-l-green-500';
        }
        
        return (
          <Card
            key={node.type}
            className={`p-3 transition-colors ${borderColor} ${
              isComingSoon
                ? 'opacity-60 cursor-not-allowed'
                : 'cursor-grab active:cursor-grabbing hover:bg-accent'
            }`}
            draggable={!isComingSoon}
            onDragStart={(e) => !isComingSoon && handleDragStart(e, node.type)}
            onDragEnd={handleDragEnd}
            onClick={() => !isComingSoon && handleNodeClick(node.type)}
          >
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Icon className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className={cn(
                    "font-medium text-sm flex-1",
                    expandedDescription !== node.type && "truncate"
                  )}>
                    {node.label}
                  </h4>
                  {isComingSoon && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-medium shrink-0">
                      Coming Soon
                    </span>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setExpandedDescription(expandedDescription === node.type ? null : node.type);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="p-0.5 hover:bg-muted rounded transition-colors shrink-0"
                    aria-label={expandedDescription === node.type ? 'Collapse description' : 'Expand description'}
                  >
                    <ChevronDown
                      className={cn(
                        "h-3.5 w-3.5 text-muted-foreground transition-transform",
                        expandedDescription === node.type && "rotate-180"
                      )}
                    />
                  </button>
                </div>
                <p className={cn(
                  "text-xs text-muted-foreground mt-0.5",
                  expandedDescription !== node.type && "line-clamp-1"
                )}>
                  {node.description}
                </p>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );

  const renderCurrentView = () => {
    // Search results
    if (searchQuery) {
      return (
        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="px-2 py-2 border-b">
            <p className="text-xs text-muted-foreground">
              {filteredNodes.length} result{filteredNodes.length !== 1 ? 's' : ''}
            </p>
          </div>
          <ScrollArea className="flex-1">
            <ScrollAreaViewport className="h-full">
              {filteredNodes.length > 0 ? (
                renderNodeList(filteredNodes)
              ) : (
                <div className="p-8 text-center text-sm text-muted-foreground">No nodes found</div>
              )}
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>
        </div>
      );
    }

    // Categories view
    if (currentView === 'categories') {
      return (
        <ScrollArea className="flex-1">
          <ScrollAreaViewport className="h-full">
            {renderCategoryView()}
          </ScrollAreaViewport>
          <ScrollAreaScrollbar orientation="vertical" />
        </ScrollArea>
      );
    }

    // Recent nodes
    if (currentView === 'recent') {
      const filteredRecentNodes = showComingSoon 
        ? recentNodesData 
        : recentNodesData.filter((node) => !node.comingSoon);
      
      return (
        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="px-2 py-2 border-b flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentView('categories')}
              className="h-8 px-2"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            <h3 className="font-medium text-sm">Recent Nodes</h3>
          </div>
          <ScrollArea className="flex-1">
            <ScrollAreaViewport className="h-full">
              {filteredRecentNodes.length > 0 ? (
                renderNodeList(filteredRecentNodes)
              ) : (
                <div className="p-8 text-center text-sm text-muted-foreground">
                  {showComingSoon ? 'No recent nodes yet' : 'No available recent nodes'}
                </div>
              )}
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>
        </div>
      );
    }

    // Filtered view (for sub-node filtering)
    if (currentView === 'filtered' && subNodeFilter) {
      const filterLabels = {
        chatModel: 'Chat Models',
        memory: 'Memory Nodes',
        tools: 'Tools',
        parser: 'Output Parsers'
      };
      
      return (
        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="px-2 py-2 border-b flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentView('categories')}
              className="h-8 px-2"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            <h3 className="font-medium text-sm">{filterLabels[subNodeFilter]}</h3>
          </div>
          <ScrollArea className="flex-1">
            <ScrollAreaViewport className="h-full">
              {allNodes.length > 0 ? (
                renderNodeList(allNodes)
              ) : (
                <div className="p-8 text-center text-sm text-muted-foreground">
                  No {filterLabels[subNodeFilter].toLowerCase()} available
                </div>
              )}
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>
        </div>
      );
    }

    // Category nodes
    const categoryNodes = nodesByCategory[currentView as keyof typeof nodesByCategory] || [];
    const filteredCategoryNodes = showComingSoon 
      ? categoryNodes 
      : categoryNodes.filter((node) => !node.comingSoon);
    const category = categories.find((c) => c.id === currentView);

    return (
      <div className="flex-1 overflow-hidden flex flex-col">
        <div className="px-2 py-2 border-b flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCurrentView('categories')}
            className="h-8 px-2"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <h3 className="font-medium text-sm">{category?.label}</h3>
        </div>
        <ScrollArea className="flex-1">
          <ScrollAreaViewport className="h-full">
            {filteredCategoryNodes.length > 0 ? (
              renderNodeList(filteredCategoryNodes)
            ) : (
              <div className="p-8 text-center text-sm text-muted-foreground">
                {showComingSoon ? 'No nodes in this category' : 'No available nodes in this category'}
              </div>
            )}
          </ScrollAreaViewport>
          <ScrollAreaScrollbar orientation="vertical" />
        </ScrollArea>
      </div>
    );
  };

  // Docked view - show only icons
  if (!isExpanded) {
    return (
      <div className="w-14 border-l bg-background flex flex-col h-full">
        {/* Expand button at top */}
        <div className="p-2 border-b flex justify-center">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsExpanded(true)}
            title="Expand Node Palette"
            className="h-8 w-8"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        </div>
        
        {/* Category icons */}
        <ScrollArea className="flex-1">
          <ScrollAreaViewport className="h-full">
            <div className="flex flex-col gap-1 p-2">
              {visibleCategoryList.map((category) => {
                const Icon = category.icon;
                return (
                  <button
                    key={category.id}
                    onClick={() => {
                      setCurrentView(category.id);
                      setIsExpanded(true);
                    }}
                    title={category.label}
                    className={cn(
                      "flex items-center justify-center p-2 hover:bg-accent transition-colors rounded-md",
                      currentView === category.id && "bg-accent"
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </button>
                );
              })}
            </div>
          </ScrollAreaViewport>
          <ScrollAreaScrollbar orientation="vertical" />
        </ScrollArea>
      </div>
    );
  }

  // Expanded view
  return (
    <div className="w-64 h-full flex flex-col bg-background border-l transition-all duration-300 overflow-hidden">
      {/* Header */}
      <div className="p-3 border-b flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-semibold text-sm">Node Palette</h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsExpanded(false)}
            title="Dock Node Palette"
            className="h-6 w-6"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search nodes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 pr-10 h-8 text-sm"
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            <Switch
              checked={showComingSoon}
              onCheckedChange={setShowComingSoon}
              title={showComingSoon ? "Show Coming Soon nodes" : "Show Available nodes only"}
              className="scale-75"
            />
          </div>
        </div>
      </div>

      {/* Content - with proper overflow */}
      {renderCurrentView()}
    </div>
  );
}

export { EnhancedNodePalette };
export default EnhancedNodePalette;
